import { DateTime } from 'luxon'
import { type Absence as TServerAbsence } from '@/api/client/models/Absence'
import type { SubstituteArray } from '@/api/client/models/SubstituteArray'
import { i18n } from '@/locales/locales'

const { t } = i18n

export class Absence {
  data: TAbsenceData

  constructor(absenceData: TAbsenceData) {
    this.data = absenceData
  }

  get startsAt(): DateTime | undefined {
    return this.data.startsAt ? DateTime.fromISO(this.data.startsAt) : undefined
  }

  get endsAt(): DateTime | undefined {
    return this.data.endsAt ? DateTime.fromISO(this.data.endsAt) : undefined
  }

  get icon() {
    return Absence.getIconFromCategory(this.data.category)
  }

  getDuration() {
    const hours = this.data.workingSeconds
      ? Math.round((this.data.workingSeconds / 3600) * 10) / 10
      : 0
    return this.data.duration === ABSENCE_DURATIONS.START_END
      ? `${hours} ${hours === 1 ? t('words.hour') : t('words.hours')}`
      : `${this.data.workingDays} ${t('words.days', {
          n: Math.floor(this.data.workingDays ?? 0),
        })}`
  }

  /*
   * Returns the payload that can be send to the backend
   * @throws Error if any required field is missing
   */
  getPayload(): TServerAbsence {
    if (!this.data.category)
      throw new Error('cannot create absence payload without category')
    if (!this.data.duration)
      throw new Error('cannot create absence payload without duration')
    if (!this.data.startsAt)
      throw new Error('cannot create absence payload without startsAt')
    if (!this.data.endsAt)
      throw new Error('cannot create absence payload without endsAt')
    return {
      id: this.data.id,
      absencecategory_name: this.data.category,
      duration: this.data.duration,
      approval: ABSENCE_STATES.PENDING,
      note: this.data.note,
      start_date: this.data.startsAt,
      end_date: this.data.endsAt,
      unassigned_uploaded_file_ids: this.data.unassignedUploadedFileIds,
      substitute_id: this.data.substituteId,
    }
  }

  toJSON(): TAbsenceData {
    return this.data
  }

  static createFromObject(
    dhAbsence: TDehydratedAbsence | TAbsenceData,
  ): Absence {
    // currently migrating the persisted data structure from TDehydratedAbsence to TAbsenceData (starting migration with version 3.6.0)
    // so for now this needs to handle both, handling for TDehydratedAbsence can be removed once migration is complete
    if (isTDehydratedAbsence(dhAbsence)) {
      return new Absence({
        id: dhAbsence.id,
        category: dhAbsence.absencecategory_name,
        state: dhAbsence.approval,
        duration: dhAbsence.duration as TAbsenceDuration,
        note: dhAbsence.note,
        startsAt: dhAbsence.starts_at,
        endsAt: dhAbsence.ends_at,
        workingDays: dhAbsence.working_days,
        workingSeconds: dhAbsence.working_seconds,
        createdAt: dhAbsence.created_at,
        attachmentUrls: dhAbsence.attachment_urls ?? undefined,
        substituteId: dhAbsence.substitute_id ?? undefined,
        substitutes: dhAbsence.substitutes ?? undefined,
      })
    } else {
      return new Absence(dhAbsence)
    }
  }

  public static createFromServerObject(absence: TServerAbsence) {
    return new Absence({
      id: absence.id,
      category: absence.absencecategory_name,
      state: absence.approval,
      duration: absence.duration as TAbsenceDuration,
      note: absence.note,
      startsAt: absence.start_date,
      endsAt: absence.end_date ?? undefined,
      workingDays: absence.working_days,
      workingSeconds: absence.working_seconds,
      createdAt: absence.created_at ?? undefined,
      attachmentUrls: absence.attachment_urls ?? undefined,
      substituteId: absence.substitute_id ?? undefined,
      substitutes: absence.substitutes ?? undefined,
    })
  }

  static getIconFromCategory(category?: TAbsenceCategory) {
    switch (category) {
      //illness
      case 'sick':
      case 'sick_industrial_accident':
      case 'sick_before_short_time_work':
      case 'sick_short_time_work':
      case 'sick_no_payment':
        return 'fas fa-first-aid'
      //holiday
      case 'vacation':
      case 'special_vacation':
      case 'excused_absent':
      case 'vacation_no_payment':
        return 'fa-regular fa-island-tropical'
      //time account
      case 'working_time_account':
      case 'short_time_work':
      case 'maternity_pay':
        return 'fas fa-user-clock'
      //school
      case 'workshop':
      case 'school':
        return 'fas fa-graduation-cap'
      //innocent
      case 'not_excused_absent':
        return 'fas fa-exclamation-triangle'
      //parents
      case 'parental_leave':
      case 'sick_child':
      case 'sick_child_no_payment':
        return 'fas fa-baby-carriage'
      //weather
      case 'weather_related_failure':
        return 'fas fa-umbrella'
      default:
        return 'fa-calendar'
    }
  }

  static getPossibleDurations(category?: TAbsenceCategory): TAbsenceDuration[] {
    // this info is derived from app/Classes/Absence/AbsenceTypeProperties.php in the Backend
    switch (category) {
      case 'sick':
        return ['full_day', 'half_day', 'start_end']
      case 'vacation':
        return ['full_day', 'half_day', 'start_end']
      case 'workshop':
        return ['full_day', 'half_day', 'start_end']
      case 'special_vacation':
        return ['full_day', 'half_day']
      case 'school':
        return ['full_day', 'half_day', 'start_end']
      case 'sick_child_no_payment':
        return ['full_day', 'half_day', 'start_end']
      case 'working_time_account':
        return ['full_day', 'half_day', 'start_end']
      case 'vacation_no_payment':
        return ['full_day', 'half_day']
      case 'holiday':
        return ['full_day', 'half_day']
      case 'parental_leave':
        return ['full_day']
      case 'weather_related_failure':
        return ['full_day', 'half_day', 'start_end']
      case 'meals_14_euro':
        return ['full_day']
      case 'meals_28_euro':
        return ['full_day']
      case 'other':
        return ['full_day']
      default:
        return ['full_day']
    }
  }
}

export const ABSENCE_CATEGORIES = {
  SICK: 'sick',
  VACATION: 'vacation',
  WORKSHOP: 'workshop',
  SPECIAL_VACATION: 'special_vacation',
  SCHOOL: 'school',
  SICK_CHILD_NO_PAYMENT: 'sick_child_no_payment',
  SICK_CHILD: 'sick_child',
  WORKING_TIME_ACCOUNT: 'working_time_account',
  VACATION_NO_PAYMENT: 'vacation_no_payment',
  HOLIDAY: 'holiday',
  PARENTAL_LEAVE: 'parental_leave',
  WEATHER_RELATED_FAILURE: 'weather_related_failure',
  MEALS_14_EURO: 'meals_14_euro',
  MEALS_28_EURO: 'meals_28_euro',
  OTHER: 'other',
}

export const ABSENCE_DURATIONS = {
  FULL_DAY: 'full_day' as const,
  HALF_DAY: 'half_day' as const,
  START_END: 'start_end' as const,
}

export const ABSENCE_STATES = {
  PENDING: 'pending',
  GRANTED: 'granted',
  DENIED: 'denied',
}

export type TAbsenceCategory =
  (typeof ABSENCE_CATEGORIES)[keyof typeof ABSENCE_CATEGORIES]
export type TAbsenceApprovalState =
  (typeof ABSENCE_STATES)[keyof typeof ABSENCE_STATES]
export type TAbsenceDuration =
  (typeof ABSENCE_DURATIONS)[keyof typeof ABSENCE_DURATIONS]

export type TAbsenceData = {
  id?: number
  category?: TAbsenceCategory
  state?: TAbsenceApprovalState
  duration?: TAbsenceDuration
  note?: string
  startsAt?: string
  endsAt?: string
  workingDays?: number
  workingSeconds?: number
  createdAt?: string
  attachmentUrls?: Array<{
    name?: string
    mimeType?: string
    url?: string
  }>
  substituteId?: number
  substitutes?: SubstituteArray
  unassignedUploadedFileIds?: string[]
  files?: FileList
}

// deprecated, do not use!
export type TDehydratedAbsence = {
  id: number
  absencecategory_name: string
  approval?: string
  duration: string
  note?: string
  starts_at: string
  ends_at: string
  working_days?: number
  working_seconds?: number
  created_at?: string
  attachment_urls?: Array<{
    name?: string
    mime_type?: string
    url?: string
  }> | null
  durationObject: string
  substitute_id?: number | null
  substitutes?: SubstituteArray | null
  uuid?: string
}

function isTDehydratedAbsence(obj: unknown): obj is TDehydratedAbsence {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    'id' in obj &&
    'absencecategory_name' in obj &&
    'duration' in obj
  )
}
